import { Injectable } from '@angular/core';
import { ErrorCodes } from '@shared';
import { LoggingService } from '@shared/logging';
import { RxDocument } from 'rxdb';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CentralDatabaseProvider } from '../central-database.provider';
import { TableInfoModel, TableTypeEnum } from '../models';
import { CentralDatabaseKeys } from '../schemas/central-database-keys';
import { addRxPlugin } from 'rxdb';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
addRxPlugin(RxDBQueryBuilderPlugin);

@Injectable()
export class TableInfoRepository {
	public constructor(
		private centralDbProvider: CentralDatabaseProvider,
		private loggingService: LoggingService
	) {}

	public async bulkInsert(tableInfos: TableInfoModel[]): Promise<void> {
		const centralDb = this.centralDbProvider.getDatabase();

		await centralDb.tableinfos.bulkInsert(tableInfos);
	}

	public getAll$(workfileId: string): Observable<TableInfoModel[]> {
		const centralDb = this.centralDbProvider.getDatabase();

		return centralDb.tableinfos
			.find()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workfileId)
			.$.pipe(map((items: any) => items.map((item: RxDocument<TableInfoModel>) => item.toJSON())));
	}

	public async exists(workfileId: string): Promise<boolean> {
		const tableInfo: RxDocument<TableInfoModel>[] = await this.centralDbProvider
			.getDatabase()
			.tableinfos.find()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workfileId)
			.exec();

		return Boolean(tableInfo.length);
	}

	public async deleteRecords(workfileId: string): Promise<void> {
		try {
			// partially deleted workfile
			// make sure there are still records to be deleted and avoid triggering an error
			const workfileRecordsExist: boolean = await this.exists(workfileId);
			if (!workfileRecordsExist) {
				return;
			}

			const deletedTableInfos = await this.centralDbProvider
				.getDatabase()
				.tableinfos.find()
				.where(CentralDatabaseKeys.workFileId)
				.eq(workfileId)
				.remove();

			if (deletedTableInfos.length === 0) {
				throw new Error('Delete workfile (table infos) failed');
			}
		} catch (error) {
			this.loggingService.error(ErrorCodes.DeleteWorkfileError, error);
			throw new Error(error);
		}
	}

	public getDataTablesCount$(): Observable<number> {
		return this.centralDbProvider
			.getDatabase()
			.tableinfos.find()
			.where(CentralDatabaseKeys.tableInfo_tableType)
			.eq(TableTypeEnum.DataTable)
			.$.pipe(map((items: RxDocument<TableInfoModel>[]) => items.length));
	}
}
